import { AttentionBlock, Button, titleCase, toast } from '@westhetics/ui'
import { useState, type ReactNode } from 'react'

/**
 * A senha temporária, mostrada uma única vez com "Copiar" (RN-ADM-10 e RN-ADM-14). Serve à criação
 * do usuário e ao reset: a senha nasce no servidor, aparece aqui e não volta a aparecer.
 */
export function TemporaryPassword({
  name,
  password,
  children,
  missing,
  className,
}: {
  /** De quem é a senha. */
  name: string
  /** `null` quando a API repetiu a resposta guardada, que vem sem a senha. */
  password: string | null
  /** A quem entregar e quando a troca é exigida. */
  children: ReactNode
  /** O que fazer quando a senha não veio. */
  missing?: ReactNode
  className?: string
}) {
  const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle')

  async function copyPassword() {
    if (!password) return
    try {
      await navigator.clipboard.writeText(password)
      setCopy('copied')
      toast('Senha copiada')
    } catch {
      setCopy('failed')
    }
  }

  return (
    <AttentionBlock
      className={className}
      title={`Senha temporária de ${titleCase(name)}`}
      aria-label="Senha temporária"
    >
      {password ? (
        <>
          <p>{children}</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <code className="border-hairline-strong bg-pampas font-display text-name text-ink border px-[14px] py-[10px] leading-none font-normal select-all">
              {password}
            </code>
            <Button variant="outline" onClick={() => void copyPassword()}>
              {copy === 'copied' ? 'Copiada' : 'Copiar'}
            </Button>
          </div>
          {copy === 'failed' ? (
            <p className="text-plum mt-2 text-[12px] leading-[1.6]">
              O navegador não deixou copiar. Selecione a senha acima e copie à mão.
            </p>
          ) : null}
        </>
      ) : (
        <p>{missing}</p>
      )}
    </AttentionBlock>
  )
}
